/**
 * App Tour Steps Configuration
 * Defines all the steps in the guided tour of the Vaiu app
 */

export interface TourStep {
  selector: string;
  title: string;
  description: string;
  gif?: string;
  tips?: string[];
}

export const tourSteps: TourStep[] = [
  {
    selector: '.user-profile-section',
    title: '👤 Your Profile',
    description: 'This is your personal profile area where you can manage your account settings, view your balance, and access your payment methods.',
    gif: '/img/tour/profile-demo.gif',
    tips: [
      'Set your personal info and decide when it is visible to others',
      'Selling anything? Set up your merchant services',
      'Manage your notifications and preferences'
    ]
  },
  {
    selector: '.balance-display',
    title: '💰 Your Balance',
    description: 'Keep track of your current balance across different currencies. This updates in real-time with every transaction.',
    gif: '/img/tour/balance-demo.gif',
    tips: [
      'Support for multiple currencies',
      'Top up your balance with your preferred payment method',
      'As a merchant, decide when to sweep your balance to your bank account'
    ]
  },
  {
    selector: '.create-token-button',
    title: '🎫 Create Cash Tokens',
    description: 'Create digital cash tokens that can be shared with others. Perfect for gifts, payments, or splitting bills.',
    gif: '/img/tour/create-token-demo.gif',
    tips: [
      'Set any amount you want',
      'Add a personal message',
      'Tokens are intentionally short-lived. Keep an eye on each token\'s expiration time',
      'The recipient can spend the token anywhere Vaiu is accepted or deposit it to their own Vaiu account.',
    ]
  },
  {
    selector: '.redeem-token-section',
    title: '🎁 Redeem Tokens',
    description: 'Enter codes to redeem cash tokens sent to you by friends, family, or businesses.',
    gif: '/img/tour/redeem-demo.gif',
    tips: [
      'Codes are case-insensitive',
      'Tokens add directly to your balance',
      'You\'ll see any message the payer included.'
    ]
  },
  {
    selector: '.payment-methods',
    title: '💳 Payment Methods',
    description: 'Manage your payment methods including credit cards, bank accounts, and digital wallets.',
    gif: '/img/tour/payment-methods-demo.gif',
    tips: [
      'Add multiple payment methods',
      'Pay without giving out your details',
      'All data is securely encrypted'
    ]
  },
  {
    selector: '.merchant-discovery',
    title: '🏪 Discover Merchants',
    description: 'Find local businesses and merchants who accept Vaiu payments. Support your local economy!',
    gif: '/img/tour/merchants-demo.gif',
    tips: [
      'Search by location or business type',
      'View ratings and reviews',
      'See special offers and discounts'
    ]
  },
  {
    selector: '.transaction-history',
    title: '📊 Transaction History',
    description: 'View all your past transactions, including payments sent, received, and tokens created or redeemed.',
    gif: '/img/tour/transactions-demo.gif',
    tips: [
      'Filter by date range or type',
      'Export transactions for records',
      'Search by merchant or recipient'
    ]
  },
  {
    selector: '.qr-code-scanner',
    title: '📱 QR Code Scanner',
    description: 'Scan QR codes to quickly pay merchants or redeem tokens without typing long codes.',
    gif: '/img/tour/qr-scanner-demo.gif',
    tips: [
      'Works with any QR code scanner',
      'Instant payment processing',
      'Perfect for in-person purchases'
    ]
  },
  {
    selector: '.referral-program',
    title: '🎁 Referral Program',
    description: 'Earn rewards by inviting friends to join Vaiu. Both you and your friend get bonus credits!',
    gif: '/img/tour/referral-demo.gif',
    tips: [
      'Share your unique referral code',
      'Track your business referral earnings',
      'Earn 10% of your friend\'s first transaction'
    ]
  },
  {
    selector: '.merchant-tools',
    title: '🛍️ Merchant Tools',
    description: 'If you\'re a business owner, access powerful tools to accept payments and manage your sales.',
    gif: '/img/tour/merchant-tools-demo.gif',
    tips: [
      'Generate invoice QR codes',
      'Track sales and revenue',
      'Manage inventory and offers'
    ]
  },
  {
    selector: '.notifications-center',
    title: '🔔 Notifications',
    description: 'Stay updated with payment confirmations, new features, and important account information.',
    gif: '/img/tour/notifications-demo.gif',
    tips: [
      'Real-time payment notifications',
      'Customize notification preferences',
      'Never miss important updates'
    ]
  },
  {
    selector: '.support-chat',
    title: '💬 Support Chat',
    description: 'Get help anytime with our integrated support chat. Our team is here to assist you!',
    gif: '/img/tour/support-demo.gif',
    tips: [
      'Available 24/7 for urgent issues',
      'FAQ section for common questions',
      'Direct line to our support team'
    ]
  },
  {
    selector: '.settings-menu',
    title: '⚙️ Settings',
    description: 'Customize your Vaiu experience with security settings, preferences, and account management.',
    gif: '/img/tour/settings-demo.gif',
    tips: [
      'Enable two-factor authentication',
      'Set spending limits',
      'Manage privacy preferences'
    ]
  },
  {
    selector: '.currency-selector',
    title: '🌍 Multi-Currency Support',
    description: 'Vaiu supports multiple currencies. Switch between USD, EUR, GBP, and more based on your location.',
    gif: '/img/tour/currency-demo.gif',
    tips: [
      'Real-time exchange rates',
      'Convert between currencies instantly',
      'Your preferred currency is saved'
    ]
  }
];

/**
 * Get tour steps filtered by user type
 */
export const getTourStepsForUser = (user: any): TourStep[] => {
  if (!user) return [];

  let steps = [...tourSteps];

  // Filter steps based on user type
  if (!user.merchantAccount) {
    // Remove merchant-specific steps for regular users
    steps = steps.filter(step => step.selector !== '.merchant-tools');
  }

  // Add role-specific steps
  if (user.roles && user.roles.includes('ADMIN')) {
    steps.push({
      selector: '.admin-panel',
      title: '👑 Admin Panel',
      description: 'Access administrative tools to manage the platform, users, and system settings.',
      gif: '/img/tour/admin-demo.gif',
      tips: [
        'Manage user accounts',
        'View system analytics',
        'Configure platform settings'
      ]
    });
  }

  return steps;
};

/**
 * Tour step categories for easier navigation
 */
export const tourCategories = {
  basics: {
    name: 'Getting Started',
    steps: ['user-profile-section', 'balance-display', 'create-token-button', 'redeem-token-section']
  },
  payments: {
    name: 'Payments & Transactions',
    steps: ['payment-methods', 'transaction-history', 'qr-code-scanner']
  },
  social: {
    name: 'Social Features',
    steps: ['referral-program', 'merchant-discovery']
  },
  merchant: {
    name: 'Merchant Tools',
    steps: ['merchant-tools']
  },
  account: {
    name: 'Account Management',
    steps: ['settings-menu', 'notifications-center', 'support-chat']
  },
  customization: {
    name: 'Customization',
    steps: ['currency-selector']
  }
}; 